import {DocumentNode, gql} from '@apollo/client';
import {Group, Select, SelectItem, Text, Tooltip} from '@mantine/core';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import {PlateSchema, PlateSchemaType, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {getSchemaOptionDataMetadata, SchemaOwnerQuery} from 'schemas/SchemaOwnerUtil';
import getTestProps from 'test/lib/cypress/getTestProps';

import {PlateSchemaSelectorOwnerQuery} from './__generated/PlateSchemaSelectorOwnerQuery';
import * as SampleSchemaActions from './SampleSchemaActions';
import SampleSchemaStore from './SampleSchemaStore';
import StorableSchemaSelector from './StorableSchemaSelector';

export interface PlateSchemaSelectorProps {
  disabled?: boolean;
  disabledMessage?: string;
  error?: string;
  // Called with `(schema | null)` if `shouldShowNoSchemaOption` is true.  Otherwise, called with `(schema)`.
  onChange: (schema: StorableSchema | null) => void;
  registryId?: string;
  plateSchemaType?: PlateSchemaType;
  selectedPlateSchemaId: string | null;
  // Marked as optional here but there is a default prop for it so we can rely on it being present in the component.
  shouldShowNoSchemaOption?: boolean;
  plateSchemas?: Array<{
    id: string;
    name: string;
  }>;
  /** Function to filter which plate schemas to show in the dropdown in addition to plateSchemaType */
  plateSchemaFilter?: (schema: PlateSchema) => boolean;
  /** Uses compact schema rows without starring controls. */
  compact?: boolean;
  /** Overrides the compact selector's display-only schema without adding it to the schema store. */
  displayedPlateSchema?: {id: string; name: string} | null;
  placeholder?: string;
}

interface PlateSchemaSelectorEnhancedProps {
  plateSchemas: Array<PlateSchema> | null;
}

class PlateSchemaSelectorConnector extends DataConnector<
  PlateSchemaSelectorProps,
  PlateSchemaSelectorEnhancedProps
> {
  override stores = [SampleSchemaStore];

  enhanceProps(): PlateSchemaSelectorEnhancedProps {
    const {registryId, plateSchemaType, plateSchemaFilter} = this.props;
    let plateSchemas = this.sampleSchemaStore().getPlateSchemas(registryId);
    if (plateSchemaType && plateSchemas) {
      plateSchemas = plateSchemas.filter((schema) => schema.plateSchemaType === plateSchemaType);
    }
    if (plateSchemaFilter != null && plateSchemas) {
      plateSchemas = plateSchemas.filter((schema) => plateSchemaFilter(schema));
    }
    return {plateSchemas};
  }
}

export const PLATE_SCHEMA_SELECTOR_OWNER_QUERY: DocumentNode = gql`
  query PlateSchemaSelectorOwnerQuery @skipAudit @engComponent(name: INVENTORY) {
    plateSchemas {
      id
      registry {
        id
        organization {
          id
          name
        }
      }
    }
  }
`;

const CLEAR_SCHEMA_VALUE = '__clear_plate_schema__';
const CLEAR_SCHEMA_GROUP = 'Clear plate schema';
const PLATE_SCHEMA_GROUP = 'Plate schemas';

interface CompactPlateSchemaSelectItemProps extends SelectItem {
  metadata?: string;
}

const CompactPlateSchemaSelectItem = React.forwardRef<HTMLDivElement, CompactPlateSchemaSelectItemProps>(
  ({label, metadata, ...others}, ref) => (
    <div ref={ref} {...others}>
      <Group position="apart" spacing="xs" noWrap={true}>
        <Text size="sm">{label}</Text>
        {metadata != null && (
          <Text color="dimmed" size="xs">
            {metadata}
          </Text>
        )}
      </Group>
    </div>
  )
);

CompactPlateSchemaSelectItem.displayName = 'CompactPlateSchemaSelectItem';

function getCompactOptions(
  schemas: Array<{id: string; name: string}> | null,
  getSchemaOwner: (schema: {id: string; name: string}) => string | undefined,
  shouldShowNoSchemaOption: boolean
): Array<CompactPlateSchemaSelectItemProps> {
  if (schemas == null) {
    return [];
  }
  const getMetadata = getSchemaOptionDataMetadata(schemas, getSchemaOwner);
  const schemaOptions = schemas.map((schema) => ({
    group: shouldShowNoSchemaOption ? PLATE_SCHEMA_GROUP : undefined,
    label: schema.name,
    metadata: getMetadata(schema),
    value: schema.id,
  }));
  return shouldShowNoSchemaOption
    ? [{group: CLEAR_SCHEMA_GROUP, label: 'No plate schema', value: CLEAR_SCHEMA_VALUE}, ...schemaOptions]
    : schemaOptions;
}

class PlateSchemaSelector extends React.Component<
  PlateSchemaSelectorProps & PlateSchemaSelectorEnhancedProps
> {
  static defaultProps = {
    shouldShowNoSchemaOption: false,
  };

  override componentDidMount(): void {
    SampleSchemaActions.fetchAllPlateSchemas();
  }

  override render(): JSX.Element {
    return (
      <SchemaOwnerQuery<PlateSchemaSelectorOwnerQuery>
        query={PLATE_SCHEMA_SELECTOR_OWNER_QUERY}
        getIdOwnerTuples={(data) =>
          data.plateSchemas.map((plateSchema) => ({
            id: plateSchema.id,
            owner: plateSchema.registry.organization.name,
          }))
        }
      >
        {({getSchemaOwner}) => {
          if (this.props.compact) {
            const schemas =
              this.props.displayedPlateSchema === undefined
                ? this.props.plateSchemas
                : this.props.displayedPlateSchema == null
                  ? []
                  : [this.props.displayedPlateSchema];
            const disabledMessage =
              this.props.disabled && this.props.disabledMessage != null
                ? this.props.disabledMessage
                : undefined;
            return (
              <Tooltip disabled={disabledMessage == null} label={disabledMessage}>
                <div>
                  <Select
                    aria-label="Plate schema"
                    data={getCompactOptions(
                      schemas,
                      getSchemaOwner,
                      Boolean(this.props.shouldShowNoSchemaOption)
                    )}
                    disabled={Boolean(this.props.disabled)}
                    error={this.props.error}
                    errorProps={getTestProps({component: 'SchemaSelector', element: 'errorMessage'})}
                    itemComponent={CompactPlateSchemaSelectItem}
                    nothingFound="No schemas found"
                    onChange={(value) => {
                      if (value === CLEAR_SCHEMA_VALUE) {
                        this.props.onChange(null);
                        return;
                      }
                      const schema = schemas?.find(({id}) => id === value);
                      if (schema != null) {
                        this.props.onChange(schema as StorableSchema);
                      }
                    }}
                    placeholder={this.props.placeholder ?? 'Select a schema...'}
                    searchable={true}
                    size="md"
                    styles={(theme) => ({
                      separator: {
                        '&:first-of-type': {display: 'none'},
                        borderTop: `1px solid ${theme.colors.gray[3]}`,
                        margin: 0,
                        padding: 0,
                      },
                      separatorLabel: {display: 'none'},
                    })}
                    value={this.props.selectedPlateSchemaId}
                    wrapperProps={getTestProps({component: 'SchemaSelector', element: 'selectDropdown'})}
                  />
                </div>
              </Tooltip>
            );
          }
          return (
            <StorableSchemaSelector
              isDisabled={this.props.disabled}
              disabledMessage={this.props.disabledMessage}
              error={this.props.error}
              onChange={this.props.onChange}
              schemas={this.props.plateSchemas}
              selectedSchemaId={this.props.selectedPlateSchemaId}
              shouldShowNoSchemaOption={this.props.shouldShowNoSchemaOption}
              getSchemaOwner={getSchemaOwner}
              placeholder={this.props.placeholder}
              // Overridden by props from redux
              starredSchemaIds={[]}
            />
          );
        }}
      </SchemaOwnerQuery>
    );
  }
}

export default PlateSchemaSelectorConnector.connect(PlateSchemaSelector);
